import * as React from "react"
import { useRef } from "react";
import {toast} from "react-toastify";
import { batch, useSignal } from "@preact/signals-react"
import { Box, styled } from "@mui/material"
import { useAppToast } from "@ui-seeker/app"
import { getSubmit, fileSubmit } from "@ui-seeker/utils"

import { 
	ButtonIconSeeker, PopoverSeeker, ButtonLoadingSeeker, TextFieldSeeker, 
	ToggleButtonSeeker, IconSeeker
} from "@ui-seeker/core"

import { sku, brand, retailer, country, resetCommonFilters } from "@signals"
import { WEBBACK_V2_API } from "@constants";
import { TABS_FIND_SKU, TABS_FIND_SKU_VALUE } from "../@constants";


const VisuallyHiddenInput = styled('input')({
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: 1,
	overflow: 'hidden',
	position: 'absolute',
	bottom: 0,
	left: 0,
	whiteSpace: 'nowrap',
	width: 1,
  });


export default function FindSkusPopover({ skuOptions, iconWeight, anchorEl }) {

	return <>
		<ButtonIconSeeker
			icon='rectangle-terminal' 
			iconWeight={iconWeight}
			tooltip="Write SKUs"
			onClick={e => {
				anchorEl.value = e.currentTarget
				e.stopPropagation()
			}}
			sx={{ m: 0, position: 'absolute', right: 95, top: 'calc(50% - 20px)', fontSize: '1.5rem', '&:hover': { cursor: 'pointer' } }}
		/>
	
		<FindElementsPopover anchorEl={anchorEl} skuOptions={skuOptions} />
	</>
}

const FindElementsPopover = ({ anchorEl, skuOptions }) => {
	const { showToast } = useAppToast()
	const skuCode = useSignal('')
	const currentTab = useSignal(TABS_FIND_SKU_VALUE.WRITE)
	return <PopoverSeeker anchorEl={anchorEl.value} onClose={e => anchorEl.value = null} onKeyDown={e => e.stopPropagation()}>
		<ToggleButtonSeeker
			value={currentTab.value}
			onChange={(_, newValue) => batch(() => {
				currentTab.value = newValue
			})}
			tabs={TABS_FIND_SKU}
		/>
		{currentTab.value == TABS_FIND_SKU_VALUE.WRITE && <Box sx={{ m: 2, minWidth: 500, display: 'flex', alignItems: 'center' }}>
			<TextFieldSeeker
				placehoder="Enter codes separated by commas..."
				value={skuCode.value}
				onChange={e => skuCode.value = e.target.value}
				icon="pen-to-square"
				sx={{
					width: 425
				}}
			/>
			<ButtonLoadingSeeker
				size="small"
				onClick={() => {
					const loadingToastId = toast.loading('Searching SKUs...');
					
					getSubmit(`${WEBBACK_V2_API}/master-data/sku/find-skus-list`, {
						name: 'name',
						codeSkus: skuCode.value,
						brand: brand.value, retailer: retailer.value, country: country.value
					}).then(res => batch(() => {
						if (!res?.selectedSkus?.length) {
							showToast.warning('Sku not found!')
						}else{
							skuOptions.value = res.options
							sku.value = res.selectedSkus
							showToast.success(`Sku successfully searched! Found ${res?.selectedSkusLength} skus out of ${res?.codeSkusLength}.`)
						}
					})).catch(e => showToast.error('Error'))
					.finally(() => {
						anchorEl.value = false
						skuCode.value = ''
						toast.dismiss(loadingToastId)
					})
				}}
				sx={{ fontSize: '0.8rem', m: 1 }}
				disabled={!skuCode.value}
			>
				Reload Skus
			</ButtonLoadingSeeker>
		</Box> }

		{currentTab.value == TABS_FIND_SKU_VALUE.UPLOAD && <UploadComponent anchorEl={anchorEl} skuOptions={skuOptions}/>}
	</PopoverSeeker>
}

const UploadComponent = ({ anchorEl, skuOptions }) =>  {

	const selectedFile = useSignal(null)
    const loading = useSignal(false)
    const { showToast } = useAppToast()
    const ref = useRef(null)

    const handleFileChange = (event) => {
		const file = event.target.files[0]
	
		if (file && file.type === 'text/csv') {
			const loadingToastId = toast.loading('Searching SKUs...');
            resetCommonFilters()
			selectedFile.value = file
			loading.value = true
			fileSubmit(`${WEBBACK_V2_API}/master-data/sku/find-skus-csv`, [event.target.files[0]])
			.then(res => {
				if (!res?.selectedSkus?.length) {
					showToast.warning('Sku not found!')
				} else {
					skuOptions.value = res.options
					sku.value = res.selectedSkus
					showToast.success(`Sku successfully searched! Found ${res?.selectedSkusLength} skus out of ${res?.codeSkusLength}.`)
				}
			}).catch(e => {
				showToast.warning('There was an issue with the file. Only .csv files are allowed. Please try again using the format suggested in the info button')
			}).finally(() => {
				loading.value = false
				anchorEl.value = false
				toast.dismiss(loadingToastId)
			})
		} else {
			showToast.warning('Only CSV files are allowed.')
			selectedFile.value = null
		}
	}

	return <Box sx={{ m: 2, minWidth: 500, display: 'flex', alignItems: 'center' }}>
		<ButtonLoadingSeeker
			size="small"
			variant="contained"
			component="label"
			startIcon={<IconSeeker icon={'cloud-arrow-up'} />}
			sx={{ fontSize: '0.8rem', m: 1 }}
			disabled={loading.value}
		>
			{loading.value ? 'Uploading...' : 'Upload CSV file'}
			<VisuallyHiddenInput
				type="file"
				ref={ref}
				onChange={handleFileChange}
			/>
		</ButtonLoadingSeeker>
		<IconSeeker 
			icon={'circle-info'} 
			sx={{ m: .5, fontSize: '1.5rem', color: 'text.disabled', "&:hover": { cursor: 'pointer' } }} 
			tooltip={'Only .csv files are allowed. Make sure the file has a single column, with one code per cell. Please follow the format suggested'}
		/>
	</Box>
}